import { Text } from '@antimatterhq/ui/components/typography';
import { useDatasourceUpgrade } from '@/contexts/DatasourceUpgradeContext';
import { DatasourceUpgradeStatusIcon } from '@/components/dataSources/upgrade/statusIcon/DatasourceUpgradeStatusIcon';
import { cn } from '@/lib/utils';
import {
  getUpgradeChangeKey,
  isTableChange,
} from '@/lib/datasource/upgrade/utils/datasourceUpgradeUtils';
import {
  DatasourceUpgradeItemDiff,
  DatasourceUpgradeMedallionDiff,
} from '@/lib/datasource/upgrade/datasourceUpgrade.types';

const RIGHT_INDENT = 25; // we have to indent the right side of the sidebar to account for the scrollbar

const UpgradeItem = ({
  indent = 0,
  diff,
}: {
  indent?: number;
  diff: DatasourceUpgradeItemDiff;
}) => {
  const { currentDiff, setCurrentDiff } = useDatasourceUpgrade();
  return (
    <div className="flex items-center">
      <div className="w-[24px]">
        <DatasourceUpgradeStatusIcon status={diff.status} hideUnapplied />
      </div>
      <Text
        className={cn(
          'flex-1 overflow-hidden text-ellipsis whitespace-nowrap',
          diff.status === 'confirmed' && 'text-grey-350 dark:text-grey-350',
          currentDiff === diff &&
            'font-medium text-blue-600 underline dark:text-blue-400',
          currentDiff !== diff && 'cursor-pointer',
        )}
        style={{ width: `${218 - RIGHT_INDENT - (indent + 1) * 24}px` }}
        onClick={() => setCurrentDiff(diff)}
      >
        {diff.title}
      </Text>
    </div>
  );
};

const UpgradeChanges = ({
  changes,
  indent = 0,
}: {
  changes: DatasourceUpgradeItemDiff[];
  indent?: number;
}) => {
  return (
    <ul className="list-none p-0">
      {changes.map((change) => {
        return (
          <li key={getUpgradeChangeKey(change)}>
            {!isTableChange(change) && (
              <UpgradeItem indent={indent} diff={change} />
            )}
          </li>
        );
      })}
    </ul>
  );
};

const UpgradeTablesChanges = ({
  step,
}: {
  step: DatasourceUpgradeMedallionDiff;
}) => {
  return (
    <div>
      {step.tables.map((table) => (
        <div key={table.tableName} className="pl-[24px]">
          <Text
            bold
            className="block overflow-hidden text-ellipsis whitespace-nowrap"
          >
            {table.tableName}
          </Text>
          <UpgradeChanges changes={table.changes} indent={1} />
          {!table.title && !table.changes.length && (
            <Text size="sm" className="pl-[24px] italic" color="info">
              no changes
            </Text>
          )}
          {table.title && <UpgradeItem diff={table} />}
        </div>
      ))}
    </div>
  );
};

export const DatasourceUpgradeSidebar = ({
  drawerHeight,
}: {
  drawerHeight: number;
}) => {
  const { bronze, silver, gold } = useDatasourceUpgrade();

  return (
    <nav
      className="absolute top-[16px] overflow-y-auto"
      aria-label="Upgrade sidebar"
      style={{ bottom: `${drawerHeight}px`, right: `${RIGHT_INDENT}px` }}
    >
      <div className="flex flex-col gap-4">
        {!!bronze.changes.length && (
          <div>
            <Text size="base" bold>
              Bronze changes
            </Text>
            <UpgradeChanges changes={bronze.changes} />
          </div>
        )}
        {silver.status !== 'unchanged' && (
          <div>
            <Text size="base" bold>
              Silver changes
            </Text>
            <UpgradeTablesChanges step={silver} />
          </div>
        )}
        {gold.status !== 'unchanged' && (
          <div>
            <Text size="base" bold>
              Gold changes
            </Text>
            <UpgradeTablesChanges step={gold} />
          </div>
        )}
      </div>
    </nav>
  );
};
